import React, {useContext} from "react";
import {useTranslation} from "react-i18next";
import {PackagesCartItem} from "types/entities/PackagesCartItem";
import {ReservationDriver} from "types/entities/ReservationDriver";
import {useTypedSelector} from "helpers/reducers";

import {ReservationDriverAssignOption} from "types/enums/specific/ReservationDriverAssignOption";
import AccessTicketDriversAssignUser from "pages/Front/Booking/BookingLogin/components/ReservationDetailComponents/AccessTicketDriversAssignUser";
import AccessTicketDriverInvitation from "pages/Front/Booking/BookingLogin/components/ReservationDetailComponents/AccessTicketDriverInvitation";
import AccessTicketEditGuest from "pages/Front/Booking/BookingLogin/components/ReservationDetailComponents/AccessTicketEditGuest";
import {Driver} from "types/entities/Driver";

import {useAssignCurrentUserToTicketHandler} from "utils/reservationUtils";
import {useReservationTicketOptions} from "utils/hooks/useReservationTicketOptions";
import {BookingLoginReservationDetailDriverRedesignContext} from "pages/Front/Booking/BookingLogin/BookingLoginReservationDetailRedesign";
import {PackageItem} from "types/entities/PackageItem";
import {PackageItemPersonType} from "types/enums/PackageItemPersonType";
import FeatherIcon from "components/FeatherIcon";

const AssignationPanelComponent = (props: {
    item: PackagesCartItem;
    reservationDriver: ReservationDriver | null;
    option: ReservationDriverAssignOption;
    onCloseClick: () => void;
    setReservationDriver: (reservationDriver: ReservationDriver) => void;
    packageItem: PackageItem | null;
    personIndex: number;
}) => {
    const {t} = useTranslation();
    const reservationContext = useContext(BookingLoginReservationDetailDriverRedesignContext);
    const selectedReservation = useTypedSelector(state => state.bookingLoginState.selectedReservation);
    const mainUser = useTypedSelector(state => state.bookingLoginState.user);
    const isMainUser: boolean = mainUser?.id === props?.reservationDriver?.driver?.id;
    const isMainUserAndRequiredDataMissing: boolean =
        isMainUser && (!props?.reservationDriver?.driver?.birthdate || !props?.reservationDriver?.driver?.nickname);
    const {isLoading, fetchReservationAssignDriver} = useAssignCurrentUserToTicketHandler();
    const {getOption} = useReservationTicketOptions();

    const assignCurrentUserToTicket = async (driver: Driver) => {
        console.log("assignCurrentUserToTicket", driver);
        if (!driver || !reservationContext || !selectedReservation) {
            return;
        }

        const result = await fetchReservationAssignDriver(driver.id, props.item.id, selectedReservation.id, props.personIndex);
        console.log(result);
        if (result) {
            props.setReservationDriver(result.reservationDriver);
            props.onCloseClick();
        }
    };

    const assignInvitedUser = (reservationDriver: ReservationDriver) => {
        props.setReservationDriver(reservationDriver);
        props.onCloseClick();
    };

    const getHeaderText = () => {
        const option = getOption(props.option);
        const heading = t("Assign user");
        const baseHeading = heading + " - " + option.label;
        const mainEditHeading = heading + " - " + t("Fill in your data");
        return isMainUserAndRequiredDataMissing ? mainEditHeading : baseHeading;
    };

    const RenderComponent = () => {
        if (!reservationContext || !selectedReservation || !reservationContext.profileDriver || !selectedReservation.reservationDrivers) {
            console.log(
                "no reservationContext or selectedReservation or reservationContext.profileDriver or selectedReservation.reservationDrivers",
                reservationContext,
                selectedReservation
            );
            return (
                <div>
                    profileDriverId: {reservationContext?.profileDriver?.id}
                    selectedReservationLength: {selectedReservation?.reservationDrivers?.length}
                </div>
            );
        }
        const reservationDrivers = selectedReservation.reservationDrivers as ReservationDriver[];
        const reservationDriverIds = reservationDrivers.map(res => res.driver_id);
        const reservationFamilyAccounts = reservationContext.profileDriver.familyAccounts;
        switch (props.option) {
            case ReservationDriverAssignOption.AssignFamily:
                return (
                    <div
                        className={"ReservationDriverAssignOption.AssignFamily flex flex-col w-full gap-y-[20px] md:gap-y-[32px] gap-x-[20px] md:gap-x-[54px]"}
                    >
                        {reservationContext.profileDriver &&
                            reservationFamilyAccounts &&
                            reservationFamilyAccounts.length > 0 &&
                            reservationFamilyAccounts
                                .filter((acc: Driver) => !reservationDriverIds.includes(acc.id))
                                .map((acc: Driver) => {
                                    return (
                                        <AccessTicketDriversAssignUser
                                            key={acc.id}
                                            driver={acc}
                                            isDisableDriver={
                                                props.packageItem?.person_type === PackageItemPersonType.Child &&
                                                acc.person_type !== PackageItemPersonType.Child
                                            }
                                            isLoading={isLoading}
                                            onClick={() => assignCurrentUserToTicket(acc)}
                                        />
                                    );
                                })}
                    </div>
                );
            case ReservationDriverAssignOption.AssignGuest:
                return <AccessTicketEditGuest reservationDriver={props.reservationDriver} context={reservationContext} item={props.item} />;
            default:
            case ReservationDriverAssignOption.InviteGuest:
                return (
                    <AccessTicketDriverInvitation
                        reservation_id={selectedReservation.id}
                        item={props.item}
                        onResult={reservationDriver => assignInvitedUser(reservationDriver)}
                    />
                );
        }
    };

    return (
        <div className={"AssignationPanelComponent px-[17px] md:px-[81px] pb-[49px] md:pb-[58px] pt-[20px] md:pt-[28px]"}>
            <button className={"py-2 px-2 -ml-2 mb-4"} type={"button"} onClick={props.onCloseClick}>
                <FeatherIcon name={"arrow-left"} className={"size-[20px] md:size-[30px] flex-none text-black"} strokeWidth={2} />
            </button>
            <div className={"pb-[20px] md:pb-[27px] flex flex-col"}>
                <h3 className={"font-semibold text-xs md:text-base text-black"}>{getHeaderText()}</h3>
                {props.option === ReservationDriverAssignOption.AssignGuest && isMainUserAndRequiredDataMissing && (
                    <h4 className={"text-arbo-red md:font-medium text-xs"}>
                        {t("Required data is missing. Please save the required data for the main account.")}
                    </h4>
                )}
            </div>
            <RenderComponent />
        </div>
    );
};

export default AssignationPanelComponent;
